import { isPhoneClassDevice } from './deviceClass';

/**
 * The single home of device-pixel-ratio policy for every GPU surface.
 *
 * Every canvas the engine renders to is sized in CSS pixels and backed at
 * `css * dpr` physical pixels, where dpr comes from exactly one of the
 * policies below. The Rust side receives (cssW, cssH, dpr) verbatim
 * (`registerEditorCanvas` / `resizeSurface` in the worker protocol) and does
 * all pixel snapping against that dpr (`ViewTransform` in
 * aqueduct/crates/editor/src/editor.rs). Pointer math on a surface must use
 * the same policy value that sized the surface.
 *
 * Every application surface has the same DPR ceiling. Surface-specific
 * rendering quality is expressed separately through material supersampling,
 * graph texture resolution, or document resolution.
 *
 * Material AA accounts for DPR rather than multiplying an independent
 * default on top of it: DPR below 1.5 defaults to SSAA 2, while DPR 1.5-2
 * defaults to SSAA 1. Explicit internal quality selections remain overrides.
 *
 */

const SURFACE_DPR_MAX = 2;
const MATERIAL_VIEWPORT_TEXTURE_DIMENSION_MAX = 8192;
const HIGH_DPR_THRESHOLD = 1.5;

function rawDpr(): number {
	return typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
}

/** Graph editor viewport (RustEditorViewport, conformance runs). */
export function editorSurfaceDpr(): number {
	return Math.min(rawDpr(), SURFACE_DPR_MAX);
}

/** Exact-texel 2D previews: Preview2D and the editor's node preview overlay. */
export function preview2dSurfaceDpr(): number {
	return Math.min(rawDpr(), SURFACE_DPR_MAX);
}

export type MaterialGridSsaaScale = 1 | 2 | 3 | 4 | 6 | 8;
const MATERIAL_GRID_SSAA_SCALES: MaterialGridSsaaScale[] = [8, 6, 4, 3, 2, 1];

/** Material viewports and landing demos (sphere/grid renders). */
export function getMaterialViewportDpr(cssWidth = 0, cssHeight = 0): number {
	if (typeof window === 'undefined') return 1;
	const baseDpr = Math.min(rawDpr(), SURFACE_DPR_MAX);
	const maxCssDimension = Math.max(cssWidth, cssHeight, 1);
	const dimensionLimitedDpr = MATERIAL_VIEWPORT_TEXTURE_DIMENSION_MAX / maxCssDimension;
	return Math.max(0.25, Math.min(baseDpr, dimensionLimitedDpr));
}

export function getDefaultMaterialGridSsaaScale(
	dpr: number = getMaterialViewportDpr()
): MaterialGridSsaaScale {
	return dpr < HIGH_DPR_THRESHOLD ? 2 : 1;
}

export function getEffectiveMaterialGridSsaaScale(
	scale: MaterialGridSsaaScale,
	canvasWidth = 0,
	canvasHeight = 0
): MaterialGridSsaaScale {
	const deviceLimitedScale = isPhoneClassDevice() && scale > 2 ? 2 : scale;
	const maxCanvasDimension = Math.max(canvasWidth, canvasHeight, 1);
	for (const candidate of MATERIAL_GRID_SSAA_SCALES) {
		if (candidate > deviceLimitedScale) continue;
		if (maxCanvasDimension * candidate <= MATERIAL_VIEWPORT_TEXTURE_DIMENSION_MAX) {
			return candidate;
		}
	}
	return 1;
}
